import type * as Highcharts from 'highcharts';
import type { ChartBuildOptions } from './bestValueLabel';

const DEFAULT_PLANT_NAME = 'PV Plant';

const RISK_ZONES: Highcharts.SeriesAreaOptions[] = [
  {
    type: 'area',
    name: 'High',
    color: 'rgba(255, 0, 0, 0.5)',
    fillColor: 'rgba(255, 0, 0, 1)',
    data: [
      [0, 0],
      [0.32, 0],
      [0.4, 0.55],
      [1, 0.7],
    ],
  },
  {
    type: 'area',
    name: 'Low',
    color: 'rgba(0, 255, 0, 0.5)',
    fillColor: 'rgba(0, 255, 0, 1)',
    data: [
      [0, 1],
      [0.32, 1],
      [0.4, 0.45],
      [1, 0.3],
    ],
  },
];

/** Keeps the plant point highlighted with its tooltip open, even after the pointer leaves the chart. */
const highlightPlantOnLoad: Highcharts.ChartLoadCallbackFunction = function () {
  const highlight = () => {
    this.series.forEach((serie, i) => serie.setState(i === 0 ? 'hover' : 'inactive'));
    this.tooltip.refresh(this.series[0].data[0]);
  };
  this.pointer.reset = () => {
    highlight();
    return undefined;
  };
  highlight();
};

const riskTooltip: Highcharts.TooltipFormatterCallbackFunction = function () {
  const x = this.point.x as number;
  const y = this.point.y as number;
  return (
    `<span style='font-size: "smaller"'><b>` +
    this.series.name +
    `</b></span><br/>` +
    '<span style="color:' +
    this.point.color +
    '">●</span>Rain Score: <b>' +
    x.toFixed(2) +
    '</b><br/>' +
    '<span style="color:' +
    this.point.color +
    '">●</span>Deposition Score: <b>' +
    y.toFixed(2) +
    '</b><br/>'
  );
};

/**
 * The plant position (rain score, deposition score) over the high and low soiling risk zones.
 * An export has no tooltip: the plant point carries its scores as a data label instead.
 */
export const buildSoilingRiskOptions = (
  scores: { rain?: number | null; deposition?: number | null },
  plantName: string | undefined,
  { forExport }: ChartBuildOptions = {},
): Highcharts.Options => ({
  chart: {
    type: 'area',
    zooming: { type: 'x' },
    ...(forExport ? {} : { events: { load: highlightPlantOnLoad } }),
  },
  title: { text: 'Soiling Risk' },
  xAxis: { title: { text: 'Rain Score' }, max: 1, gridLineWidth: 1 },
  yAxis: [{ title: { text: 'Deposition Score' }, max: 1, gridLineWidth: 1 }],
  legend: { enabled: true },
  tooltip: { valueDecimals: 2, formatter: riskTooltip },
  credits: { enabled: false },
  plotOptions: { area: { stacking: 'normal', marker: { enabled: false } } },
  series: [
    {
      type: 'scatter',
      name: plantName ? plantName : DEFAULT_PLANT_NAME,
      data: [[scores.rain as number, scores.deposition as number]],
      color: 'black',
      ...(forExport ? { dataLabels: { enabled: true, format: '{series.name}: {point.x:.2f}, {point.y:.2f}' } } : {}),
    },
    ...RISK_ZONES,
  ],
});
